import { DateFormat, formatDate, isAllDay as isAllDayExperience, isFuture, now } from "common";
import dayjs from "dayjs";
import { groupBy, map, sumBy, uniqBy } from "lodash";
import {
    Event,
    EventGroupIdPlaceHolder,
    EventOption,
    Experience,
    ExperienceOption,
    ResourceUsage,
    Seller,
    Timeslot,
    User,
} from "types";
import { getSeller } from "../utils/getSeller";
import { allowMultipleEventsPerTimeslot, getOptions, isAlwaysPrivateExperience } from "./Experience";
import { canOpenRoster, getId } from "./Timeslot";
import { queryClient } from "@/api/queryClient";

// The backend applies at most one option per event today; a turned-off row (deletedAt) never counts.
export const getEventOptionRow = (event?: Pick<Event, "options">): EventOption | undefined =>
    event?.options?.find((row) => !row.deletedAt);

export const getEventOption = (event?: Event, experience?: Experience): ExperienceOption | null => {
    const optionId = getEventOptionRow(event)?.optionId;
    return optionId ? getOptions(experience).find((option) => option.id === optionId) ?? null : null;
};

export type OptionTargetEvent = Pick<Event, "id" | "experience" | "options">;

export interface RemoveTarget {
    eventId: ID;
    eventOptionId: ID;
}

// Only an event's own Event Option names its option; a timeslot selection or an unstamped event has none.
export const getRemoveTargets = (events: Pick<Event, "id" | "options">[]): RemoveTarget[] =>
    events.flatMap((event) => {
        const eventOptionId = getEventOptionRow(event)?.id;
        return eventOptionId ? [{ eventId: event.id, eventOptionId }] : [];
    });

// An event holds at most one Event Option, so a turned-off one with no live one means the seller removed it.
export const isManuallyRemoved = (event: Pick<Event, "options">): boolean =>
    !getEventOptionRow(event) && (event.options?.some((eventOption) => eventOption.deletedAt) ?? false);

export const isFutureEvent = (event: Event): boolean => isFuture(getStart(event));

// A timeslot can carry an event id whose Event never loaded, so both lists merge by id with the loaded
// Event first; past events are dropped so a month-view selection never changes historical events.
export const getOptionTargetEvents = (events: Event[], timeslots: Timeslot[]): OptionTargetEvent[] =>
    uniqBy(
        [
            ...events.filter((event) => isFutureEvent(event)),
            ...timeslots
                .filter((timeslot) => timeslot.event?.id && isFuture(timeslot.arrivalDatetime))
                .map((timeslot) => ({ id: timeslot.event.id, experience: { id: timeslot.product } })),
        ],
        "id",
    );

export const isSingleExperienceSelection = (events: OptionTargetEvent[]): boolean =>
    events.length > 0 && events.every((event) => event.experience.id === events[0].experience.id);

export const isAllDay = (event: Event, experience: Experience) => {
    const startTime = Number(formatDate(getStart(event), DateFormat.TIME_SHORT));
    return startTime === 0 && isAllDayExperience(experience);
};

export const getBlackoutTimeRanges = (event: Event) => {
    return [
        {
            startTime: Number(formatDate(getStart(event), DateFormat.TIME_SHORT)),
            endTime: Number(formatDate(getStart(event).add(5, "minute"), DateFormat.TIME_SHORT)),
        },
    ];
};

export const getStart = (event: Event) => {
    // This date/time from the field `start` is timezone agnostic, but has `+00:00` appended as the offset.
    // Since `parseDates` interceptor already converted the date taking the timezone offset into account,
    // we need to change it back to UTC and convert to seller timezone ignoring the offset to get the correct value.
    const utcStart = dayjs(event.start).tz("UTC");
    const { timezoneName } = getSeller();
    return utcStart.tz(timezoneName, true);
};

export const getCreatedFromNow = (altText: string, user?: User, event?: Event) => {
    const createdBy = user?.name ? `Created by ${user.name} ` : "";
    const fromNow = event?.createdAt?.fromNow();

    return fromNow ? `${createdBy}${fromNow}` : altText;
};

export const canStartVirtualMeeting = (event?: Event, experience?: Experience) => {
    if (event && experience) {
        const startAt = getStart(event).subtract(experience?.virtualMeeting?.startOffset ?? 0, "minutes");
        return now().isBetween(startAt, getEnd(event).add(experience.duration, "minutes"));
    }

    return false;
};

export const getEnd = (event: Event) => {
    // This date/time from the field `end` is timezone agnostic, but has `+00:00` appended as the offset.
    // Since `parseDates` interceptor already converted the date taking the timezone offset into account,
    // we need to change it back to UTC and convert to seller timezone ignoring the offset to get the correct value.
    const utcEnd = dayjs(event.end).tz("UTC");
    const { timezoneName } = getSeller();
    return utcEnd.tz(timezoneName, true);
};

/**
 * Per v1 behavior:
 * Always allow assigning guides and equipment if the event is not a private placeholder event.
 *
 * @param seller
 * @param event
 * @param timeslot
 * @returns {boolean}
 */
export const allowGuideAndInventoryAssign = (seller: Seller, event?: Event, timeslot?: Timeslot): boolean => {
    if (timeslot && !canOpenRoster(timeslot)) {
        return false;
    }

    return !isPrivatePlaceholder(seller, event, timeslot);
};

/**
 * Check if an event is a private placeholder event, this happens when MPE is enabled
 *
 * @param seller
 * @param event
 * @param timeslot
 * @returns
 */
export const isPrivatePlaceholder = (seller: Seller, event?: Event, timeslot?: Timeslot) => {
    if (event?.groupId) {
        return event.groupId === EventGroupIdPlaceHolder.GROUP_ID_PRIVATE_PLACEHOLDER_EVENT;
    }

    if (event && event.quantity.reserved > 0) {
        return false;
    }

    const experienceId = event?.experience.id ?? timeslot?.product;

    if (experienceId) {
        const experience = queryClient.getQueryData<Experience>(["products", experienceId]);
        if (experience) {
            return isAlwaysPrivateExperience(experience) && allowMultipleEventsPerTimeslot(seller, experience);
        }
    }

    return false;
};

export const getTimeslotIdByEvent = (event: Event) => {
    let time: number | null = Number(formatDate(getStart(event), DateFormat.TIME_SHORT));
    if (time === 0) {
        const experience = queryClient.getQueryData<Experience>(["products", event.experience.id]);
        if (experience && isAllDay(event, experience)) {
            time = null;
        }
    }

    return getId({
        date: formatDate(getStart(event)),
        time: time,
        product: event.experience.id,
        event: { id: event.id },
    });
};

export const hasBookings = (event: Event): boolean => {
    return !!(event.quantity.reserved || event.quantity.confirmed || event.quantity.pending);
};

export const isEventReservedByItem = (itemId: ID, event?: Event): boolean => {
    const purchaseDrivenUsages = (event?.resourceUsages ?? []).filter((usage) => usage.purchaseItem);

    return purchaseDrivenUsages.length > 0 && purchaseDrivenUsages.every((usage) => usage.purchaseItem?.id === itemId);
};

/**
 * Usages are stored one per booked unit, so callers that show or count equipment need them rolled up
 * per resource — "Kayak (x4)", not four rows.
 */
export const getAggregatedResourceUsages = (resourceUsages?: ResourceUsage[]): ResourceUsage[] => {
    return map(groupBy(resourceUsages ?? [], "resource.id"), (usages) => ({
        ...usages[0],
        count: sumBy(usages, "count"),
    })).filter((usage) => usage.count > 0);
};
